/**
 * The route table (TanStack Router, code-based — no generator, no plugin).
 *
 *     /login              LoginView            public
 *     (_authed)           AppLayout            the guard: signed out → /login?redirect=<here>
 *       /                 → /rundowns
 *       /rundowns         RundownsView
 *       /rundowns/$id     RundownEditorView  (keyed by the id: another rundown is a fresh session)
 *       /whoami           WhoamiView
 *
 * ADDING A PAGE: write the view in `src/views/` (a container: hooks in,
 * presentational components out), add a `createRoute({ getParentRoute: () =>
 * authedRoute, path, component })` below and list it in `routeTree`, and — if
 * it belongs in the top bar — add it to `NAV` in `views/AppLayout.tsx`.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect, type RouterHistory } from '@tanstack/react-router';
import { Toaster } from '../components/ui/sonner.js';
import { AppLayout } from '../views/AppLayout.js';
import { LoginView } from '../views/LoginView.js';
import { RundownEditorView } from '../views/RundownEditorView.js';
import { RundownsView } from '../views/RundownsView.js';
import { WhoamiView } from '../views/WhoamiView.js';
import { useTheme } from './hooks.js';
import type { Services } from './services.js';
import { guardAuthed, HOME_PATH } from '../lib/redirect.js';

export type RouterContext = { readonly services: Services; readonly queryClient: QueryClient };

function RootLayout() {
  const theme = useTheme();
  return (
    <>
      <Outlet />
      <Toaster theme={theme} position="bottom-right" />
    </>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => (typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {}),
  component: LoginView,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ context, location }) => {
    const bounce = guardAuthed(context.services.sessions.getSession(), location.href);
    if (bounce !== null) throw redirect(bounce);
  },
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: HOME_PATH });
  },
});

const rundownsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/rundowns', component: RundownsView });
const whoamiRoute = createRoute({ getParentRoute: () => authedRoute, path: '/whoami', component: WhoamiView });

const rundownEditorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rundowns/$rundownId',
  component: function RundownEditorPage() {
    const { rundownId } = rundownEditorRoute.useParams();
    return <RundownEditorView key={rundownId} rundownId={rundownId} />;
  },
});

export const routeTree = rootRoute.addChildren([loginRoute, authedRoute.addChildren([indexRoute, rundownsRoute, rundownEditorRoute, whoamiRoute])]);

/** `history`: the browser's in the app, a memory history in tests (the router is otherwise the real one). */
export function createAppRouter(context: RouterContext, history: RouterHistory) {
  return createRouter({ routeTree, context, history, defaultPreload: false });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
